/** The desk's tabs, which data source each one reads, and whether a title has that source. */
import type { DeskPub, Meta, TitleAvail } from '../api/visitorsDesk'
import type { DeskTab } from './deskPeriod'

export const DESK_TABS: { id: DeskTab; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'sources', label: 'Sources' },
  { id: 'search', label: 'Search' },
  { id: 'flows', label: 'Flows' },
  { id: 'articles', label: 'Articles' },
  { id: 'issues', label: 'Issues' },
  { id: 'audience', label: 'Audience' },
  { id: 'trends', label: 'Trends' },
  { id: 'revenue', label: 'Revenue' },
]

/** `mixed` tabs gate per panel; `orders` always shows. */
export const TAB_SOURCE: Record<DeskTab, 'first_party' | 'gsc' | 'mixed' | 'orders'> = {
  today: 'mixed', sources: 'first_party', search: 'gsc', flows: 'first_party', articles: 'first_party',
  issues: 'first_party', audience: 'first_party', trends: 'mixed', revenue: 'orders',
}

export const DESK_PUBS: DeskPub[] = ['flight_journal', 'model_airplane_news', 'rc_car_action', 'diecast', 'all']

export function isDeskPub(s: string | undefined): s is DeskPub {
  return !!s && (DESK_PUBS as string[]).includes(s)
}

export function isDeskTab(s: string | undefined): s is DeskTab {
  return !!s && DESK_TABS.some((t) => t.id === s)
}

export type { TitleAvail }

const NOT_LIVE = 'Starts when the new site goes live'
export const CANT_CHECK = 'Couldn’t check availability'
const NO_GSC = 'No Search Console data yet'

/** `search` may carry its leading `?` or not. */
export function deskPath(pub: string, tab: string, search = ''): string {
  const q = search.replace(/^\?/, '')
  return `/visitors/desk/${pub}/${tab}${q ? `?${q}` : ''}`
}

export function tabAvailable(tab: DeskTab, pub: string, availability: TitleAvail[] | null): { ok: boolean; text?: string } {
  const source = TAB_SOURCE[tab]
  if (source === 'mixed' || source === 'orders') return { ok: true }
  if (!availability) return { ok: false, text: CANT_CHECK }
  const has = (a: TitleAvail) => (source === 'gsc' ? a.gsc : a.first_party)
  const why = (a: TitleAvail | undefined) =>
    source === 'gsc' ? NO_GSC : a?.first_party_text || NOT_LIVE
  if (pub === 'all') {
    if (availability.some(has)) return { ok: true }
    return { ok: false, text: why(availability[0]) }
  }
  const a = availability.find((x) => x.publication === pub)
  if (a && has(a)) return { ok: true }
  return { ok: false, text: why(a) }
}

/** The frame's "Includes … Not yet: …" lists for a tab. First-party tabs use
 *  the frame's own (/bots) meta; Search (GSC) derives them from availability,
 *  and carries no "Rollups through" line (`rollups: false`). Null while loading. */
export function frameIncluded(
  tab: DeskTab, meta: Pick<Meta, 'titles_included' | 'titles_excluded'> | undefined, availability: TitleAvail[] | undefined,
): (Pick<Meta, 'titles_included' | 'titles_excluded'> & { rollups: boolean }) | null {
  if (TAB_SOURCE[tab] === 'gsc') {
    if (!availability) return null
    return {
      titles_included: availability.filter((a) => a.gsc).map((a) => a.publication),
      titles_excluded: availability.filter((a) => !a.gsc).map((a) => ({ publication: a.publication, reason: NO_GSC })),
      rollups: false,
    }
  }
  return meta ? { titles_included: meta.titles_included, titles_excluded: meta.titles_excluded, rollups: true } : null
}
